import React, { useState, useEffect } from 'react';
import { getGlobalOverrides, saveGlobalOverrides, GlobalDataOverrides } from '../../utils/globalOverrides';

interface GlobalOverridesPanelProps {
  adminEmail?: string;
  adminName?: string;
}

export const GlobalOverridesPanel: React.FC<GlobalOverridesPanelProps> = ({
  adminEmail = 'erueuzairue@gmail.com',
  adminName = 'Ikpemhi Oshioke',
}) => {
  const [overrides, setOverrides] = useState<GlobalDataOverrides>(() => getGlobalOverrides());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [festivalTitle, setFestivalTitle] = useState(overrides.festivalTitle);
  const [festivalSubtitle, setFestivalSubtitle] = useState(overrides.festivalSubtitle);
  const [royalMotto, setRoyalMotto] = useState(overrides.royalMotto);
  const [heroGreeting, setHeroGreeting] = useState(overrides.heroGreeting);
  const [secretariatEmail, setSecretariatEmail] = useState(overrides.secretariatEmail);
  const [activeWhatsapp, setActiveWhatsapp] = useState(overrides.activeWhatsapp);
  const [totalVillagesLabel, setTotalVillagesLabel] = useState(overrides.totalVillagesLabel);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = saveGlobalOverrides({
      festivalTitle,
      festivalSubtitle,
      royalMotto,
      heroGreeting,
      secretariatEmail,
      activeWhatsapp,
      totalVillagesLabel,
    }, `${adminName} (${adminEmail})`);

    setOverrides(updated);
    showToast('✨ Global Data Overrides updated & deployed app-wide in real-time!');
  };

  const handleReset = () => {
    if (confirm('Reset all global data overrides to factory defaults?')) {
      const reset = saveGlobalOverrides({
        festivalTitle: 'ERUE FESTIVAL 2026',
        festivalSubtitle: 'The Sacred Heritage & Cultural Celebration of Uzairue Kingdom',
        royalMotto: 'One People, One Kingdom, One Destiny',
        heroGreeting: 'Welcome to the Official Uzairue Kingdom & ERUE Festival Portal',
        secretariatEmail: 'erueuzairue@gmail.com',
        activeWhatsapp: '+2348059079068',
        totalVillagesLabel: '22 Villages & Component Communities',
      }, `${adminName} (${adminEmail})`);
      setOverrides(reset);
      setFestivalTitle(reset.festivalTitle);
      setFestivalSubtitle(reset.festivalSubtitle);
      setRoyalMotto(reset.royalMotto);
      setHeroGreeting(reset.heroGreeting);
      setSecretariatEmail(reset.secretariatEmail);
      setActiveWhatsapp(reset.activeWhatsapp);
      setTotalVillagesLabel(reset.totalVillagesLabel);
      showToast('🔄 Restored default global overrides.');
    }
  };

  return (
    <div className="space-y-6 text-slate-800">
      {toastMessage && (
        <div className="bg-emerald-900 text-emerald-100 p-4 rounded-xl shadow-lg border border-emerald-500 text-xs font-bold flex items-center justify-between">
          <span>{toastMessage}</span>
          <button onClick={() => setToastMessage(null)} className="text-emerald-300 hover:text-white cursor-pointer">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-950 text-white p-6 rounded-3xl border border-amber-400/40 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-3xl">⚙️</span>
            <div>
              <h3 className="text-xl font-serif font-black text-amber-300 uppercase tracking-wide">
                General Settings • Global Data Overrides Panel
              </h3>
              <p className="text-xs text-emerald-200">
                Replace mock data labels, headers, and default text across the festival website in real-time.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold text-xs uppercase tracking-wider rounded-xl border border-slate-700 transition-all cursor-pointer"
          >
            Reset Defaults
          </button>
        </div>
      </div>

      {/* Overrides Form */}
      <form onSubmit={handleSave} className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Festival Title (Header / Branding)
            </label>
            <input
              type="text"
              value={festivalTitle}
              onChange={(e) => setFestivalTitle(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none font-bold"
              required
            />
            <p className="text-[11px] text-gray-500">Displayed in navigation, top bars, and official portal headers.</p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Festival Subtitle / Tagline
            </label>
            <input
              type="text"
              value={festivalSubtitle}
              onChange={(e) => setFestivalSubtitle(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Kingdom Motto
            </label>
            <input
              type="text"
              value={royalMotto}
              onChange={(e) => setRoyalMotto(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none font-serif italic"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Hero Banner Greeting
            </label>
            <input
              type="text"
              value={heroGreeting}
              onChange={(e) => setHeroGreeting(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Official Secretariat Email
            </label>
            <input
              type="email"
              value={secretariatEmail}
              onChange={(e) => setSecretariatEmail(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Active WhatsApp Dispatch Line
            </label>
            <input
              type="text"
              value={activeWhatsapp}
              onChange={(e) => setActiveWhatsapp(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none font-mono"
              required
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide block">
              Total Communities Banner Label (Replaces legacy 21 clans text)
            </label>
            <input
              type="text"
              value={totalVillagesLabel}
              onChange={(e) => setTotalVillagesLabel(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-amber-500 outline-none font-bold text-emerald-800"
              required
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Last updated by <strong className="text-slate-800">{overrides.lastUpdatedBy}</strong> on {new Date(overrides.lastUpdatedAt).toLocaleString()}
          </div>
          <button
            type="submit"
            className="px-6 py-3 bg-[#0F5132] hover:bg-[#0a3822] text-amber-300 font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-lg"
          >
            💾 Save & Apply Overrides
          </button>
        </div>
      </form>
    </div>
  );
};

export default GlobalOverridesPanel;
